import { Button } from "@/components/ui/button.tsx";
import { useToast } from "@/components/ui/use-toast.ts";
import {
  exportReferrals,
  formatReferralType,
  getDaysSinceReferral,
  ReferralType,
} from "@/lib/api/referrals.ts";
import { format } from "date-fns";
import { Download, Loader2 } from "lucide-react";
import { useState } from "react";

interface ExportReferralsButtonProps {
  filters: {
    search?: string;
    teenId?: string;
    dateFrom?: string;
    dateTo?: string;
    type?: string;
  };
}

const escapeCSVField = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export function ExportReferralsButton({ filters }: ExportReferralsButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const data = await exportReferrals(filters);

      if (!data?.referrals || data.referrals.length === 0) {
        toast({
          variant: "destructive",
          title: "No data found",
          description: "There are no referrals matching the current filters.",
        });
        return;
      }

      const headers = [
        "Teen Name",
        "Referral Date",
        "Recipient",
        "Referral Source",
        "Days Since Referral",
        "Type",
        "Notes",
        "Created At",
      ];

      const csvRows = [
        headers.join(","),
        ...data.referrals.map((referral) => {
          const teenName = referral.title.split(" - ")[1] || "Unknown";
          const referralDate = referral.metadata.referralDate;
          let formattedDate: string;
          try {
            formattedDate = format(new Date(referralDate), "yyyy-MM-dd");
          } catch {
            formattedDate = referralDate;
          }
          const recipient = referral.metadata.referralRecipient || "";
          const source = referral.createdBy.name || "";
          const daysSince = getDaysSinceReferral(referralDate);
          const referralType = formatReferralType(
            referral.metadata.referralType as ReferralType,
          );
          const notes = referral.metadata.internalNote || "";
          let createdAt: string;
          try {
            createdAt = format(
              new Date(referral.createdAt),
              "yyyy-MM-dd HH:mm",
            );
          } catch {
            createdAt = referral.createdAt;
          }

          return [
            escapeCSVField(teenName),
            formattedDate,
            escapeCSVField(recipient),
            escapeCSVField(source),
            daysSince.toString(),
            escapeCSVField(referralType),
            escapeCSVField(notes),
            createdAt,
          ].join(",");
        }),
      ].join("\n");

      const blob = new Blob([csvRows], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `referrals-export-${format(new Date(), "yyyy-MM-dd")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export successful",
        description: `Exported ${data.referrals.length} referrals.`,
      });
    } catch {
      toast({
        title: "Export failed",
        description: "Failed to export referrals. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outline"
      className="gap-2"
      onClick={handleExport}
      disabled={isExporting}
    >
      {isExporting ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Exporting...
        </>
      ) : (
        <>
          <Download className="h-4 w-4" />
          Export
        </>
      )}
    </Button>
  );
}
